/**
 * ratingService.ts
 * Notas (1 a 5 estrelas) de audiobooks e livros, guardadas no D1 (tabelas "rating-content" e
 * "rating-content-summary") pelos endpoints /api/ratings e /api/ratings/summary.
 * O token Supabase é anexado automaticamente pelo apiAuthFetch.
 */
export type RatingContentType = 'audiobook' | 'book';
export interface RatingSummary { avg: number; count: number }

const SUMMARY_TTL_MS = 2 * 60 * 1000;
const summaryCache = new Map<string, { value: RatingSummary | null; at: number }>();
const key = (type: RatingContentType, id: string) => `${type}:${id}`;

const validId = (id: string) => !!id && id.length <= 200 && !/[\/\\,]/.test(id);

/** Notas estimadas de vários itens de uma vez (um pedido por página, com cache curto). */
export async function fetchRatingSummaries(type: RatingContentType, ids: string[]): Promise<Record<string, RatingSummary>> {
  const out: Record<string, RatingSummary> = {};
  const missing: string[] = [];
  for (const id of Array.from(new Set(ids.map(String))).filter(validId)) {
    const c = summaryCache.get(key(type, id));
    if (c && Date.now() - c.at < SUMMARY_TTL_MS) { if (c.value) out[id] = c.value; }
    else missing.push(id);
  }
  for (let i = 0; i < missing.length; i += 60) {
    const chunk = missing.slice(i, i + 60);
    try {
      const res = await fetch(`/api/ratings/summary?type=${type}&ids=${encodeURIComponent(chunk.join(','))}`);
      if (!res.ok) continue;
      const data = await res.json();
      const got: Record<string, RatingSummary> = data?.summaries || {};
      for (const id of chunk) {
        const v = got[id] || null;
        summaryCache.set(key(type, id), { value: v, at: Date.now() });
        if (v) out[id] = v;
      }
    } catch { /* sem nota: a interface só não mostra */ }
  }
  return out;
}

/** Notas que o próprio usuário deu (precisa estar logado). */
export async function fetchMyRatings(type: RatingContentType, ids: string[]): Promise<Record<string, number>> {
  const clean = Array.from(new Set(ids.map(String))).filter(validId).slice(0, 100);
  if (!clean.length) return {};
  try {
    const res = await fetch(`/api/ratings?type=${type}&ids=${encodeURIComponent(clean.join(','))}`);
    if (!res.ok) return {};
    const data = await res.json();
    return data?.ratings || {};
  } catch {
    return {};
  }
}

export async function rateContent(input: {
  contentId: string; type: RatingContentType; rating: number; title?: string; author?: string; language?: string;
}): Promise<RatingSummary | null> {
  const res = await fetch('/api/ratings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      content_id: String(input.contentId),
      content_type: input.type,
      rating: input.rating,
      title: input.title,
      author: input.author,
      language: input.language
    })
  });
  if (!res.ok) throw new Error(`rating ${res.status}`);
  const data = await res.json();
  const summary: RatingSummary | null = data?.summary && data.summary.count > 0 ? data.summary : null;
  summaryCache.set(key(input.type, String(input.contentId)), { value: summary, at: Date.now() });
  return summary;
}

/** "4,3" no idioma da interface. */
export const formatRating = (avg: number, lang?: string) =>
  avg.toLocaleString(lang || undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
